/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { FullProduct } from '@msdyn365-commerce/commerce-entities';
import { CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput } from '@msdyn365-commerce/core';
import { ProductListsDataActions } from '@msdyn365-commerce/retail-proxy';

import getFullProducts, { FullProductInput } from './get-full-products';
import { ArrayExtensions } from './utilities';
import { IQuickOrderTemplateLinesState, QuickOrderTemplateLine } from './utilities/quick-order-template-line';
import { getProductDetailsCriteriaFromActionInput } from './utilities/utils';

/**
 * Input class for the GetProductListLines data action.
 *
 */
export class GetProductListLinesInput implements IActionInput {
    public productListId: string;

    public searchText: string;

    public constructor(productListId: string, searchText?: string) {
        this.productListId = productListId;
        this.searchText = searchText ?? '';
    }

    /**
     * Get Cache Key.
     * @returns - Cache key string.
     */
    public getCacheKey = (): string => `GetProductListLines|${this.productListId}|${this.searchText}`;

    /**
     * Get Cachetype Name.
     * @returns - Cache name string.
     */
    public getCacheObjectType = (): string => 'GetProductListLines';

    /**
     * Get Cachetype.
     * @returns - CacheType enum.
     */
    public dataCacheType = (): CacheType => 'none';
}

/**
 * CreateInput method for the GetProductListLines method.
 * @param productListId - ProductList Id.
 * @param searchText - Search Text.
 * @returns - Input data for getproductlistlines.
 */
export const createGetProductListLinesInput = (productListId: string, searchText?: string): GetProductListLinesInput => {
    return new GetProductListLinesInput(productListId, searchText);
};

/**
 * The action method for the GetProductListLines data action.
 * @param input - The action input.
 * @param context - The action context.
 * @returns - Product list lines.
 */
export async function getProductListLinesAction(input: GetProductListLinesInput, context: IActionContext): Promise<IQuickOrderTemplateLinesState> {
    if (!context.requestContext.user.isAuthenticated) {
        throw new Error('Unable to get full product list lines. User is not authenticated.');
    }

    const quickOrderTemplateLinesState: IQuickOrderTemplateLinesState = {
        lines: [],
        totalCount: 0
    };

    const promise = ProductListsDataActions.getProductListLinesAsync({
        callerContext: context
    }, input.productListId, input.searchText);

    const productListLines = await promise;
    const productInputs: FullProductInput[] = [];

    if (ArrayExtensions.hasElements(productListLines)) {
        // eslint-disable-next-line length-extensions-check -- Need to check length.
        quickOrderTemplateLinesState.totalCount = promise.metadata.count ?? quickOrderTemplateLinesState.lines.length;
        productListLines.map((listLine) => {
            if (listLine.ProductId) {
                quickOrderTemplateLinesState.lines.push(new QuickOrderTemplateLine(listLine));
                productInputs.push(new FullProductInput(listLine.ProductId,
                    context.requestContext.apiSettings, getProductDetailsCriteriaFromActionInput(context)));
            }
            return productInputs;
        });
        if (ArrayExtensions.hasElements(productInputs)) {
            const fullProductsInfo = await getFullProducts(productInputs, context)
                .then((products: FullProduct[]) => {
                    return products;
                })
                .catch((error: Error) => {
                    context.trace(error.message);
                    context.telemetry.error('[getProductListLinesAction] Unable to retrieve product info.');
                });
            if (fullProductsInfo) {
                quickOrderTemplateLinesState.lines.map(line => {
                    const fullProduct = fullProductsInfo.find(product => product.ProductDetails.RecordId === line.productListLine.ProductId);
                    if (!fullProduct) {
                        context.telemetry.error('[getProductListLinesAction] Unable to find product info for an product list line.');
                        return null;
                    }
                    line.fullProduct = fullProduct;
                    return null;
                });
            }
            return quickOrderTemplateLinesState;
        }
        context.trace(quickOrderTemplateLinesState.lines.length.toString());
        return quickOrderTemplateLinesState;
    }
    return quickOrderTemplateLinesState;
}

/**
 * The GetProductListLines Data Action
 * Returns order templates.
 */
export const getProductListLinesActionDataAction = createObservableDataAction<IQuickOrderTemplateLinesState>({
    id: '@msdyn365-commerce-modules/retail-actions/get-product-list-lines',
    action: getProductListLinesAction as IAction<IQuickOrderTemplateLinesState>,
    input: createGetProductListLinesInput
});

export default getProductListLinesActionDataAction;
